import { getByPath } from '@de/utils/interpolation/getByPath';
import type { PathSegment } from '@de/utils/interpolation/types';

/**
 * Parse a plain dotted-key path into {@link PathSegment}s, rejecting any
 * `[*]` wildcard. Used by {@link extractIteratorArray} to honor the
 * SAD §10 contract that `platform.dataRepeater`'s `iteratorPath` is a
 * plain dot path — wildcards make per-row iteration ambiguous (which
 * level do we iterate?), so we reject up-front instead of guessing.
 *
 * Returns `null` on any syntax problem (empty segment, `[*]`, leading or
 * trailing dot). Callers translate `null` into "render nothing" plus a
 * dev console warning.
 */
function parsePlainDotPath(path: string): PathSegment[] | null {
  if (path.length === 0) return [];
  if (path.includes('[')) return null;
  const parts = path.split('.');
  const segments: PathSegment[] = [];
  for (const part of parts) {
    if (part.length === 0) return null;
    segments.push({ type: 'key', name: part });
  }
  return segments;
}

/**
 * Resolve `iteratorPath` against the resolved data-source payload and
 * return the array we will iterate over.
 *
 * Contract:
 * - `iteratorPath` MUST be a plain dot path (`'data.programs'`). Empty
 *   string / `undefined` means "iterate `sourceData` itself".
 * - `[*]` wildcard segments are rejected — iteration ambiguity (which
 *   level fans out?) is a design smell at authoring time. Validators
 *   should catch these at publish-time too (SAD §10).
 * - The path MUST resolve to an `Array`. Anything else returns `null`,
 *   which the repeater translates into the empty / loading / error
 *   state per source status (never an inline render error).
 *
 * Soft-failure rationale: a bad `iteratorPath` is an authoring bug,
 * not a runtime exception. We log once via `console.warn` so authors
 * notice in dev/staging, but production never throws — matches the
 * binding-resolver's "missing paths resolve to undefined" stance
 * (ADR-12).
 */
export function extractIteratorArray(
  sourceData: unknown,
  iteratorPath: string | undefined,
): unknown[] | null {
  const path = iteratorPath ?? '';
  const segments = parsePlainDotPath(path);
  if (segments === null) {
    // eslint-disable-next-line no-console
    console.warn(
      `[platform.dataRepeater] Invalid iteratorPath "${path}" — must be a plain dot path with no [*] wildcards.`,
    );
    return null;
  }
  const resolved = getByPath(sourceData, segments);
  return Array.isArray(resolved) ? resolved : null;
}
